import type { TFunction } from 'i18next';
import { orgThemeErrorResponseSchema } from '@aica/api-types/schemas';
import { ApiError, apiErrorBody } from '@/lib/fetch-utils';

/** Which write failed: a new theme, an edit of an existing one, archive/restore. */
export type ThemeWriteAction = 'create' | 'edit' | 'status';

/**
 * What to say about a refused theme-set request — by the refusal's `code`.
 * Synchronous over a body already read, so the list (whose error carries its
 * parsed body) and the writes (`themeErrorMessage`) say the same thing.
 *
 * `words.fallback` — a failure that is not a known refusal: «could not save»
 * next to a write, «could not load» for the list; `words.forbidden` — a bare
 * role refusal: «may not change» for a write, «no access» for the list.
 */
export function themeRefusalMessage(
  raw: Record<string, unknown> | null,
  status: number | undefined,
  t: TFunction,
  words: { fallback: string; forbidden: string },
  action: ThemeWriteAction = 'create'
): string {
  const { fallback } = words;
  // Refusals that are not the set's own come first: a grant narrower than the
  // organization (the capability gate) and a role without the right.
  if (raw?.code === 'capability_required') {
    return t('dashboard:voicePage.themes.errors.narrowGrant');
  }
  const parsed = raw ? orgThemeErrorResponseSchema.safeParse(raw) : null;
  const refusal = parsed?.success ? parsed.data : null;
  if (!refusal) {
    return status === 403 ? words.forbidden : fallback;
  }
  switch (refusal.code) {
    case 'theme_set_full':
      return t('dashboard:voicePage.themes.errors.setFull', {
        cap: refusal.cap ?? '',
      });
    case 'theme_duplicate':
      // «Restore it instead» answers a new theme; a rename of an existing one
      // only learns that an archived theme holds the name.
      if (refusal.theme?.status === 'archived') {
        return t(
          action === 'edit'
            ? 'dashboard:voicePage.themes.errors.duplicateArchivedRename'
            : 'dashboard:voicePage.themes.errors.duplicateArchived',
          { name: refusal.theme.name }
        );
      }
      return t('dashboard:voicePage.themes.errors.duplicate', {
        name: refusal.theme?.name ?? '',
      });
    case 'theme_changed':
      return t('dashboard:voicePage.themes.errors.changed');
    case 'theme_not_found':
      return t('dashboard:voicePage.themes.errors.notFound');
    case 'theme_status_conflict':
      return t('dashboard:voicePage.themes.errors.statusConflict');
    case 'themes_disabled':
      return t('dashboard:voicePage.themes.errors.themesDisabled');
    case 'tier_not_eligible':
    case 'feature_disabled':
      return t('dashboard:voicePage.themes.errors.locked');
    case 'theme_invalid':
      return refusal.fields?.includes('name')
        ? t('dashboard:voicePage.themes.errors.invalidName')
        : refusal.fields?.includes('definition')
          ? t('dashboard:voicePage.themes.errors.invalidDefinition')
          : fallback;
  }
}

/**
 * What to say about a failed theme-set write, next to the button that caused
 * it. The body is read once (`apiErrorBody`).
 */
export async function themeErrorMessage(
  error: unknown,
  t: TFunction,
  action: ThemeWriteAction
): Promise<string> {
  return themeRefusalMessage(
    await apiErrorBody(error),
    error instanceof ApiError ? error.status : undefined,
    t,
    {
      fallback: t('dashboard:voicePage.themes.errors.generic'),
      forbidden: t('dashboard:voicePage.themes.errors.forbidden'),
    },
    action
  );
}
